import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Film, ArrowRight, Calendar, Users, Lock } from "lucide-react";
import { API, GOLD, fmtDay, ClipThumb, CoachNote } from "./recapKit";

const SharePrompt = ({ token, onDone }) => {
  const [busy, setBusy] = useState(false);
  const answer = async (allow) => {
    setBusy(true);
    try {
      await axios.post(`${API}/me/share-prompt`, { allow }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(allow ? "Sharing on — teammates can now see your highlights." : "Got it — your highlights stay private.");
      onDone();
    } catch { toast.error("Couldn't save that. Please try again."); setBusy(false); }
  };
  return (
    <motion.div data-testid="dash-share-prompt" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }} className="mb-4 rounded-lg border border-[#D4AF37]/50 p-4" style={{ background: "rgba(212,175,55,0.08)" }}>
      <p className="text-sm text-white/90 flex gap-2"><Users size={15} className="shrink-0 mt-0.5" style={{ color: GOLD }} /> Coach Barker posted a highlight from your session. Want your teammates to see it too?</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid="dash-share-allow" disabled={busy} onClick={() => answer(true)} className="min-h-[44px] px-5 rounded-sm bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-60">Allow Sharing</button>
        <button data-testid="dash-share-keep-private" disabled={busy} onClick={() => answer(false)} className="inline-flex items-center gap-1.5 min-h-[44px] px-5 rounded-sm border border-white/20 text-white/75 hover:text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-60"><Lock size={12} /> Keep Private</button>
      </div>
    </motion.div>
  );
};

export const SharePromptBanner = ({ token }) => {
  const [show, setShow] = useState(false);
  useEffect(() => { axios.get(`${API}/me/recaps`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setShow(!!data.share_prompt)).catch(() => {}); }, [token]);
  return <AnimatePresence>{show && <SharePrompt token={token} onDone={() => setShow(false)} />}</AnimatePresence>;
};

export const LatestRecapPanel = ({ token }) => {
  const navigate = useNavigate();
  const [d, setD] = useState(undefined);
  useEffect(() => { axios.get(`${API}/me/recaps`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setD(data)).catch(() => setD({ recaps: [] })); }, [token]);
  if (d === undefined) return null;
  const r = d.recaps[0];
  const open = () => navigate(`/recaps/${r.id}`);
  return (
    <motion.section data-testid="dash-latest-recap" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mb-4 rounded-2xl border border-white/10 border-l-4 border-l-[#D4AF37] bg-[#1A1A1A] p-5">
      <div className="flex items-center gap-2 mb-3">
        <Film size={15} style={{ color: GOLD }} />
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-white/60">Latest Recap</span>
        {r && <button data-testid="dash-all-recaps" onClick={() => navigate("/recaps")} className="ml-auto inline-flex items-center gap-1 min-h-[44px] text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] hover:text-white transition-colors">All recaps <ArrowRight size={11} /></button>}
      </div>
      <AnimatePresence>{d.share_prompt && <SharePrompt token={token} onDone={() => setD((x) => ({ ...x, share_prompt: false }))} />}</AnimatePresence>
      {!r ? <p data-testid="dash-recap-empty" className="text-sm text-white/55">Your first session recap will appear here after your next training session with Coach Barker.</p> : (
        <div className="flex gap-4 items-start">
          {r.clip && <ClipThumb clip={r.clip} token={token} testid="dash-recap-thumb" onClick={open} className="w-28 sm:w-36 aspect-[9/16] shrink-0" />}
          <div className="flex-1 min-w-0 space-y-2">
            <div data-testid="dash-recap-name" className="font-headings text-xl sm:text-2xl uppercase tracking-wide truncate">{r.name}</div>
            <div className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-white/45"><Calendar size={11} /> {fmtDay(r.date)}</div>
            {r.clip?.move_label && <div data-testid="dash-recap-move" className="font-mono text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: GOLD }}>{r.clip.move_label}</div>}
            {r.clip?.note && <div className="line-clamp-3"><CoachNote note={r.clip.note} testid="dash-recap-note" /></div>}
            <button data-testid="dash-view-recap" onClick={open} className="inline-flex items-center gap-2 min-h-[44px] px-5 rounded-sm border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs font-bold uppercase tracking-wider transition-colors">View Full Recap <ArrowRight size={13} /></button>
          </div>
        </div>
      )}
    </motion.section>
  );
};
